import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Form } from "react-bootstrap";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../../../config";
import "./SalesInventoryView.css";

// "Sales Inventory View" widget — a read-only stock lookup for sales staff.
//
// Data: GET /Dashboard/api/Dashboard/SalesInventory -> result[0] = {
//   totalCount, inStockCount, asOfDate, items[]
// }
//
// Unlike the outstanding-work circles this is a reference tool, not an alert, so
// it has no threshold and no red state — a big number here is good news. It is
// read-only by construction: the API reads Business Central through a client with
// no write verb, so nothing here can modify an item.
//
// Scope is Nancy-managed material. Business Central holds further material items
// that were never synced from Nancy and therefore have no Nancy ID; those are
// deliberately out of scope, which is the likely answer if someone reports an
// item missing from the list.

const COLUMNS = [
  { key: "nancyId", label: "Nancy ID" },
  { key: "manufacturerName", label: "Manufacturer" },
  { key: "style", label: "Style" },
  { key: "color", label: "Color" },
  { key: "privateName", label: "Private Name" },
  { key: "store", label: "Store", type: "select" },
  { key: "productStructure", label: "Product Structure", type: "select" },
  { key: "uomSi", label: "UOM SI", type: "select" },
  { key: "quantityOnHand", label: "Qty on Hand", type: "number" },
  { key: "qtyOnPurchaseOrder", label: "Qty on Purch. Order", type: "number" },
  { key: "qtyOnSalesOrder", label: "Qty on Sales Order", type: "number" },
  // On hand minus committed. Last column because it is the conclusion the two
  // preceding numbers lead to, and it is the one most readers will stop at.
  { key: "available", label: "Available", type: "number" },
];

// Quantities carry up to four decimals in BC. Trailing zeros are noise in a
// column someone is scanning, so they are trimmed rather than padded.
const fmtQty = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "";
  return n
    .toFixed(4)
    .replace(/\.?0+$/, "")
    .replace(/^$/, "0");
};

// One filter row beneath the headers, matching the Existing Orders list and the
// other widget modals: free text for most columns, a dropdown of the values
// present for low-cardinality ones. Client-side because the list is a few
// hundred rows.
const FilterableTable = ({ columns, rows, emptyText }) => {
  const [text, setText] = useState({});

  const options = useMemo(() => {
    const acc = {};
    columns
      .filter((c) => c.type === "select")
      .forEach((c) => {
        acc[c.key] = Array.from(
          new Set(rows.map((r) => r[c.key]).filter((v) => v))
        ).sort();
      });
    return acc;
  }, [columns, rows]);

  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((c) => {
          const q = (text[c.key] || "").trim().toLowerCase();
          if (!q) return true;
          // A dropdown selection is an exact match; free text is a substring.
          // Without that split, selecting store "CO-DEN" would also match a
          // hypothetical "CO-DEN-2".
          if (c.type === "select") {
            return String(row[c.key] ?? "").toLowerCase() === q;
          }
          return String(row[c.key] ?? "")
            .toLowerCase()
            .includes(q);
        })
      ),
    [rows, columns, text]
  );

  return (
    <>
      <div className="siv-result-count">
        Showing {filtered.length} of {rows.length}
      </div>
      <div className="siv-modal-scroll">
        <Table striped bordered hover size="sm" className="siv-modal-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.type === "number" ? "siv-num" : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
            <tr className="siv-filter-row">
              {columns.map((c) => (
                <th key={c.key}>
                  {c.type === "select" ? (
                    <Form.Select
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    >
                      <option value="">All</option>
                      {(options[c.key] || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="siv-modal-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => (
                <tr key={`${row.nancyId || "x"}-${i}`}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={[
                        c.type === "number" ? "siv-num" : "",
                        c.key === "available" ? "siv-available" : "",
                        c.key === "available" && Number(row[c.key]) < 0 ? "siv-short" : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined}
                    >
                      {c.type === "number" ? fmtQty(row[c.key]) : row[c.key] || ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const SalesInventoryView = ({ auth: { auth } }) => {
  const token = auth && auth[0] && auth[0].token;
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/SalesInventory`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setData(rows[0] || null);
        setStatus(rows[0] ? "ok" : "error");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Sales Inventory View:", err.message);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const loaded = status === "ok" && data;
  const items = (loaded && data.items) || [];
  const totalCount = loaded ? Number(data.totalCount) || 0 : null;
  const inStockCount = loaded ? Number(data.inStockCount) || 0 : null;

  return (
    <div className="siv-widget">
      <button
        type="button"
        className={`siv-button ${loaded ? "siv-calm" : "siv-unknown"}`}
        onClick={() => loaded && setShowDetails(true)}
        disabled={!loaded}
        title={
          loaded
            ? "View material inventory"
            : "Sales Inventory View is unavailable"
        }
      >
        <span className="siv-label">Sales Inventory View</span>
        <span className="siv-value">
          {status === "loading" ? "…" : loaded ? totalCount : "—"}
        </span>
        <span className="siv-caption">material items</span>
        <span className="siv-subcount">
          {loaded ? `${inStockCount} in stock` : ""}
        </span>
      </button>

      {loaded && <div className="siv-subnote">View details</div>}

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="siv-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sales Inventory View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="siv-section-title">
            Material Items ({items.length})
          </h5>
          <FilterableTable
            columns={COLUMNS}
            rows={items}
            emptyText="No material items found."
          />
          <p className="siv-footnote">
            Read-only. Quantities are read live from Business Central at the moment this
            was opened. <strong>Available</strong> is on hand minus what is committed to
            sales orders — stock on purchase order is excluded, because it is not sellable
            until it arrives. A negative Available means more has been promised than is
            held.
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(SalesInventoryView);
